// Every rule number in one place. This is a PROTOTYPE ruleset - the first
// playable loop of Matt's co-op civilization game - and every constant here
// is a dial we expect to turn after real plays. Nothing is sourced from a
// rulebook because there isn't one yet; docs/DESIGN.md is the design.

export const ROLES = ['culture', 'trade', 'science', 'military'];

export const ROLE_INFO = {
  // The id stays 'culture'; the people call it the Hall - where the town
  // decides who it is: custom, law, festival, story, and the government.
  culture: {
    label: 'Hall',
    tagline: 'The people, the customs, and the government',
    // The role's purpose in one plain line, kept on screen under the role
    // tab and in the tutorial (the learner, 2026-10-12: every run forgot
    // what its role was FOR; Matt: "make it clear what the roles are each
    // doing").
    job: 'Keep the town happy, grow it, and hand out extra workers.',
    // What one worker from the dawn split buys this role (dawn and the tutorial).
    worker: 'For the Hall, one worker is two cards, dealt when the day starts. Playing a card is free.',
    blurb: 'You play the Hall\'s cards onto its council. They make happiness and people, hand out extra workers, and carry the town\'s grievances until they are settled.',
    colour: '#d9a441',
  },
  // The id stays 'trade'; the people call it the Stewards - the economy,
  // not just the last step of it.
  trade: {
    label: 'Stewards',
    tagline: 'The city, the stores, and what the town makes',
    job: 'Run the city: the food, goods and coin everyone lives on.',
    worker: 'For the Stewards, one worker is two shifts: two half-days of work in buildings.',
    blurb: 'You run the city: fields, workshops, markets, granaries. Every worker you are given goes into a building, and the town eats, sells and stores what comes out. Take in land for room; build granaries so stores keep.',
    colour: '#4fa3c7',
  },
  // The id stays 'science'; the people call it the Scholars. They deduce
  // the natures of the world's principles by experiment, and a
  // technology is a reaction they must find.
  science: {
    label: 'Scholars',
    tagline: 'Experiments, theories, and the tree',
    job: 'Work out the principles; invent technologies for all.',
    worker: 'For the Scholars, one worker pays for one mix, one study or one ponder.',
    blurb: 'You mix the eight principles two at a time and learn what they share. A technology is a reaction: find the pair that makes it. Past tier 2, technologies are made in machines the Stewards build, each with a hidden law. Publish theories; the night proves or debunks them. Every technology changes someone else\'s board.',
    colour: '#8f7ad6',
  },
  // The id stays 'military' (saves, roles maps); the people call it the Frontier.
  military: {
    label: 'Frontier',
    tagline: 'Units, the map, and what is out there',
    job: 'Lead the units: explore, settle, fight off raiders.',
    worker: 'For the Frontier, one worker is two actions: each one is a unit moving a step, hunting or fighting.',
    blurb: 'You command units on the map: warbands that fight, scouts that see far, pioneers that found villages. They explore unknown land and deal with the camps and the other peoples.',
    colour: '#c95d4a',
  },
};

// The round (Matt, 2026-09-14 late: "allocate, then simultaneous"):
//   DAWN  the group divides the hands that turned out among the four roles
//   DAY   every role plays its own game with its hands, all at once
//   TALLY everything comes back to the middle: what the day made
//   EVENT the card turns; a decision is everyone's
//   then the night (upkeep) and a new dawn.
export const PHASES = {
  DAWN: 'dawn',
  DAY: 'day',
  TALLY: 'tally',
  EVENT: 'event',
  FINISHED: 'finished',
};

export const MIN_PLAYERS = 1;
export const MAX_PLAYERS = 4;

// ------------------------------------------------------------ the civ at start
export const START = {
  food: 4,
  goods: 2,
  coin: 2,
  knowledge: 0,
  workers: 0,
  population: 4,
  happiness: 5,
  threat: 2,
  markets: 0,
  libraries: 0,
  farms: 0,
};

export const HAPPINESS_MAX = 10;
export const THREAT_MAX = 10;
// HOUSING (Matt, after playtest 11: "make housing a thing the stewards have
// to build... it gives the stewards another thing to manage"). The town holds
// as many people as it has Houses for: the city starts with three (homes for
// 6, four people in them), and each one the Stewards build adds 2. Nobody is
// born, or moves in, past it. POPULATION_MAX is only the hard ceiling now.
export const HOUSING = { base: 0, perHouse: 2 };
export const POPULATION_MAX = 40;
// A CROWD GRUMBLES LOUDER (Matt: "maybe there should be cards whose cost
// goes up as population goes up"). A Hall card marked `crowd` that already
// costs happiness costs one more for every `per` people past `from`.
export const CROWD = { from: 4, per: 4 };

// THE TOWN DRIFTS (2026-09-21, Matt: "we probably need other ways for those
// things to be degrading over time"). Every night happiness above this fades
// by one - a glow does not keep - so keeping a town glad is the Hall's
// standing job, not a thing done once. And a glad, fed town GROWS on its
// own: a family arrives on a die roll when the larder and the mood allow.
export const HAPPINESS_FADE_ABOVE = 5; // was 6 (after playtest 12: "no free warmth")
// ...and above THIS it fades by two (playtest 6: a Hall adding three a day
// held the town at 10 all game against a fade of one; "we were at 10
// happiness the whole time").
export const HAPPINESS_FADE_FAST_ABOVE = 8;
// A town THIS glad turns out an extra worker at dawn - and with the wants and
// the fade it is hard to stay up there ("it needs to be harder to get to that").
export const GLAD_WORKER_AT = 9;
export const BIRTH = { happiness: 8, food: 6, eats: 3, on: 5 }; // d6 at or over `on`

// Coin has to buy the scarce thing. Hire craftsmen at a market: coin in,
// goods out - and every hire after the first today costs `step` more, so a
// rich town can always spend but never at a flat rate.
export const HIRE = { coin: 3, goods: 2, step: 2 };
// Dice against raiders come from TECHNOLOGY (Matt, 2026-09-28: "villages,
// towns, and the city rolling dice against the enemies should all be based
// on developing appropriate technologies"): The Watch gives the city its
// first die, Palisades gives villages one, Masonry gives towns one; the
// Wall, Steel and the Standing Army add more. Nothing rolls for free.
export const GARRISON_BASE = 0;
// Pay a camp to break up: coin per point of its strength. Diplomacy halves it.
export const PARLEY_PER_STRENGTH = 2;
// ...and it is a GAMBLE, not a purchase (Matt, after playtest 12: "it'd be
// nice if it was more variable. I don't like that it's just like a
// guarantee"). A d6 at or over this and the coin works; under it, they take
// the coin and nothing changes.
export const PARLEY_HOLDS_ON = 3;

// Workers turn out on their own at the start of every round: one per
// person, or half that when the people are unhappy (happiness at or below
// this). They go home at the end of the round whether used or not.
export const UNHAPPY_AT = 3;
// Culture's Levy: press the people for extra hands this round, at a price.
export const LEVY = { workers: 2, happiness: -1 };
// The Hall: a card given to a counselor face down costs this (playtest 6).
export const FACEDOWN_COST = { workers: 1 };
// Haggling: half a day, and a die of coin. Worse than working a market and
// it needs no market, which is the whole of its argument. The die runs
// 0-2 (playtest 11, Matt: "it seems like it should roll 0 to 2, so that you
// have a chance of getting more but you're risking something"): a face of
// nothing is the risk.
export const HAGGLE_DIE = 3; // 1, 2, 3 (playtest 14: a zero twice in a night "felt like a waste"; a die that can pay nothing is not a trade)
export const HAGGLE_BASE = 0; // faces read base+1 .. base+faces: 1, 2, 3
// Pondering: a worker, and a die of knowledge. Worse than a library shift
// on average, and it needs no library - same bargain as haggling.
// Ponder rolls 0-2 (Matt, playtest 7: "make Ponder 0-2" - at 1-2 it
// dominated Study's flat 1 for the same worker).
export const PONDER_DIE = 3;
export const PONDER_BASE = 0; // 1, 2, 3 since playtest 14: Ponder never rolls nothing
// The Hall may take up one idea a day without a card saying so (playtest 7:
// "deck building is super fun, let's lean into it"). Cards add more.
export const HALL_BUYS_PER_DAY = 1;
// A moot: when a chair is empty and nobody has emerged, two from the square
// stand; the Hall backs one and a die of MOOT_WIN or more seats them.
export const MOOT_WIN = 4;

// Trade's Buy Grain: coin into the granary, the famine valve (playtest 3).
export const GRAIN = { coin: 2, food: 2 };
// The hunt is dice: each worker rolls this many faces, plus the base.
// Irrigation raises the base by one (2-4 instead of 1-3).
export const HUNT_DIE = { faces: 3, base: 0 };
export const IRRIGATION_BONUS = 1;
// Military's Clear a Field: one more worker may hunt or farm each turn.
export const FIELD_COST = { workers: 1, goods: 1 };

// ----------------------------------------------------------------- upkeep
// Food eaten per round: ceil(population / 2). Going short is a famine.
export const FOOD_PER_PERSON = 0.5;
export const FAMINE_HAPPINESS = 2;
export const FAMINE_POPULATION = 1;
// The world never sits still: every camp and den on the map grows by this
// each night it is left alone. (THREAT is now the sum of what is out
// there; see engine/game.js menaces.)
export const THREAT_PER_ROUND = 1;
// The band: the Frontier's people on the map.
// `max` is the SMALL number now: two to begin with, and the military line
// of the tech tree buys the rest (Tracking, Bronze, Iron, Steel, one each).
// Matt, 2026-09-19: "we need limits on how big these groups can get and
// those limits should be expandable with technologies... maybe only two at
// first and then a tech of some sort makes it three."
// What is left of the old single band: the numbers that still describe how
// fighting and seeing work. `start`, `max` and the raid thresholds went with
// the band itself - units are TRAINED by the Stewards with goods now, and a
// camp sends raiders rather than reaching out and bleeding the town from a
// distance. The 2026-09-20 tuning pass that set `sight: 2` is kept; it was
// measured over ~2,400 games and the reason still holds (camps were being
// found long after they mattered).
export const BAND = {
  hitOn: 4, marchRange: 1, sight: 2,
};
// An outpost is a building, and it should cost like one. Matt, 2026-09-19:
// "I think raise an outpost should be more expensive. Just thematically, it
// seems like it should be two workers and two goods."
export const OUTPOST_COST = { goods: 2, workers: 2 };

// ---------------------------------------------------------------- the units
// The Frontier commands PIECES now, not one band. A unit's `strength` is its
// size, its dice and its hit points at once, so there is one number to read
// and one number to reduce. See engine/units.js and docs/DESIGN.md.
//
// The types differ in what they can DO, not in having their own stat
// systems: a scout moves two and cannot attack, a pioneer founds a village
// and is spent doing it, a warband fights.
export const UNIT_KINDS = {
  scout: {
    name: 'Scout',
    steps: 2,          // two hexes for one action
    ignoresRough: true,
    sight: 1,          // sees one further than anybody else
    canAttack: false,  // it defends itself and often dies doing it
    canFound: false,
    trainCost: { goods: 1 },
    startStrength: 1,
    maxStrength: 1,
  },
  warband: {
    name: 'Warband',
    steps: 1,
    canAttack: true,
    canFound: false,
    trainCost: { goods: 2 },
    startStrength: 3,  // (2 until 2026-09-21: they died twice as often as they killed)
    maxStrength: 4,    // Iron and Steel raise this
  },
  pioneer: {
    name: 'Pioneer',
    steps: 1,
    canAttack: false,
    canFound: true,
    // 2 goods + 1 food (playtest 14: "it's so hard to build this guy"; the
    // village "was so expensive that we never actually used it").
    trainCost: { goods: 2, food: 1 },
    startStrength: 1,
    maxStrength: 1,
  },
};
export const UNIT_ORDER = ['warband', 'scout', 'pioneer'];
// What each kind is FOR, said on the board wherever a unit is (playtest 9:
// "it doesn't tell me what the pioneer's for"; Matt: units must state their
// purpose).
export const UNIT_PURPOSE = {
  warband: 'Fights camps, raiders and rivals, and holds ground.',
  scout: 'Two hexes an action, any ground, and sees further. Can\'t attack.',
  pioneer: 'Walk it to open ground and Found a village there. It is spent when it does.',
};
// What one more point of strength costs when reinforcing a warband at home.
// Trashing a card (the Hall's cull) costs this: playtest 13, grievances were
// too easy to be rid of when a trash a day was free.
export const CULL_COST = { coin: 1 };
export const REINFORCE_COST = { goods: 3 }; // was 2: a point of strength is ~3 shifts (docs/ECONOMY.md)
// How strong a warband may be trained or reinforced to, before the tree.
export const WARBAND_CAP = 4;

// What each kind of camp sends out at night.
export const RAIDER_KINDS = {
  camp: { name: 'Raiders', strength: 2 },
  den: { name: 'Wolves', strength: 1 },
  wolves: { name: 'Wolves', strength: 1 },
};
// A camp left standing sends BIGGER raiders as well as more of them: +1
// strength for every this-many nights of age, up to `growMax` more. This is
// what makes the camp worth walking to. (2026-09-21: the Frontier was
// clearing a camp once in four games and swatting twelve raiders instead.)
export const RAIDER_GROW = { every: 5, max: 2 };
// THE THREAT CHANGES SHAPE WITH THE AGE (Matt, after playtest 12: "the shape
// of the threats changing is good"). The Founding: camps, dens and wolves.
// Bronze: the other peoples raid more often. Iron: raiders hit harder, and a
// big enough band lays SIEGE - it sits on a village or the city night after
// night instead of striking once and scattering, until a warband breaks it.
// Classical: invaders land from the sea at the edge of the known.
export const THREAT_SHAPE = {
  ageStrength: [0, 0, 1, 2], // added to every raider, by age
  rivalRaidsMore: 1, // from the Bronze Age, on a hostile people's raid roll
  siegeAge: 2, siegeAt: 4, // from the Iron Age, a band this strong besieges
  siegeBurnsAfter: 3, // nights a besieged village holds before it burns
  invasionAge: 3, invasionEvery: 6, invasionStrength: 5,
};
// A CAMP SUPPORTS ONLY SO MANY (Matt, 2026-09-28: "each raider camp should
// only be able to support a certain number of raiders. Like, maybe 2? And
// it should take them a while to produce them"): at most `cap` of its
// raiders abroad at once, and one sent every `sendEvery` nights it is
// able to, instead of a die every night.
export const CAMP = { cap: 2, sendEvery: 3 };
// CAMPS COME BACK (Matt, after playtest 9: "we saw one raider camp, and then
// we never saw another evidence of any raiders again"). Camps had come only
// from Raiders cards, and a summer that dealt none (one in four) left the
// Frontier nothing to do all year. Now the fog breeds them, and it breeds
// more each year: "it's okay if the first year isn't that contentious... as
// long as it then scales and we start to feel the difficulty as we go up in
// the years." Per year (the last row holds after): from which month, every
// how many months (to November), and while fewer than how many stand.
// Camps start at RAIDER_KINDS.camp.strength, +1 in year 3 and every
// `strongerEvery` years after.
export const CAMP_RETURN = {
  byYear: [
    { from: 7, every: 12, maxOut: 1 }, // year 1: one late replacement, if the first is gone
    { from: 3, every: 3, maxOut: 2 },
    { from: 2, every: 2, maxOut: 3 },
  ],
  lastMonth: 11,
  strongerEvery: 2,
};
export const campStrengthIn = (year, base) => base + Math.floor((year - 1) / CAMP_RETURN.strongerEvery);
// A warband that has won this many fights is a VETERAN: it hits one better.
export const VETERAN_AT = 2;
// What a warband is called as the tree arms it (Matt: "warband -> spearmen
// with Bronze -> legion with Iron").
export const WARBAND_TIERS = [['steel', 'Knights'], ['iron', 'Legion'], ['bronze', 'Spearmen']];
// A family at the gate can be put to work instead of taken in: this many
// workers at the next dawn, and they move on.
export const FAMILY_WORKERS = 3;
// The camp that is out there from the start (playtest 6) begins its count
// this far below zero: quiet for that many nights, then busier each night
// like any other. A fuse, so the first thing the Frontier does is walk.
export const START_CAMP_AGE = -3;

// --------------------------------------------------------------- the rivals
// OTHER PEOPLES on the island, with their own seats, land, strength and
// temper. Matt, 2026-09-21: "civilizations that also have technologies and
// their own motivations... actual adversaries competing for goals."
// See engine/rivals.js.
// THE YEAR (2026-09-27): a campaign is ~10 years of 12 months, so the
// rivals grow on a calendar tuned for ~120 nights, not 60. Full strength
// around year 6-7 (was night 24), full size around year 5.
export const RIVAL = {
  count: 2,
  startPower: 2,
  maxPower: 8,
  growEvery: 12,       // nights per +1 power (a year a point: 8 by year 7)
  levelEvery: 20,      // nights per +1 level (their technology)
  expandEvery: 6,      // nights per hex claimed (9 held by year 5)
  maxHeld: 9,
  tradeEvery: 3,       // a friendly rival sends a caravan this often
  trade: { goods: 2, coin: 1 },
  demandEvery: 6,      // a hostile rival makes demands this often
  findsYouAt: 6,       // nights before a people you have not found finds YOU
  shareEvery: 4,       // a friendly rival's scholars share a clue this often
  raidUnder: 2,        // hostile: d6 at or under min(this, age/3) sends a raider
  peaceAt: 1,          // beaten down to this power, they sue for peace
  parleyPerPower: 2,   // coin per point of power to improve their temper
  loot: 4,
};
// Each people has a COLOUR (playtest 6, Matt: "I don't even know what the
// rivals are. They definitely need to be distinct"): their seat, every
// house on their ground, their chip on the map and their line on the table
// all wear it, so a people is a colour before it is a name.
// ...and a WAY: what makes them THEM (Matt, after playtest 9: "make the
// other civilizations have more meaning and be much clearer and more
// distinct"). `way` overrides RIVAL's dials for that people; `wayText` is
// what the table and the phones say about it, word for word.
export const RIVAL_NAMES = [
  {
    id: 'ashen', name: 'the Ashen', colour: '#8d949c', blurb: 'Charcoal burners of the high forest.',
    wayText: 'They grow strong fast: +1 strength every 8 nights.', way: { growEvery: 8 },
  },
  {
    id: 'reed', name: 'the Reed People', colour: '#4faa6e', blurb: 'Fishers and weavers of the shore. Slow to anger, slow to forgive.',
    wayText: 'Slow to anger: a raid cools them one step, not straight to hostile. Their carts bring food.', way: { slowAnger: true, trade: { food: 3 } },
  },
  {
    id: 'tall', name: 'the Tall Kings', colour: '#d3a93f', blurb: 'A line of chiefs. They expect gifts.',
    wayText: 'They expect gifts: demands come even while they are wary, and a gift or parley warms them two steps.', way: { demandsWhenWary: true, warmBy: 2 },
  },
  {
    id: 'salt', name: 'the Salt Clans', colour: '#63b3d1', blurb: 'Traders first. They will deal.',
    wayText: 'Traders: when friendly, carts of coin every other night; parley with them costs half.', way: { tradeEvery: 2, trade: { coin: 2, goods: 1 }, parleyHalf: true },
  },
  {
    id: 'painted', name: 'the Painted Folk', colour: '#b866c9', blurb: 'Hunters who mark the trees to claim land.',
    wayText: 'Their marks spread: every 3 nights they claim another hex, up to 12. Whatever the Frontier has not yet found on that ground goes to them.', way: { expandEvery: 3, maxHeld: 12 },
  },
  {
    id: 'iron', name: 'the Iron Sons', colour: '#c4582f', blurb: 'They smelted iron before you did.',
    wayText: 'Smiths: their raiders start a level stronger, and when friendly their carts bring goods.', way: { startLevel: 1, trade: { goods: 3 } },
  },
];
export const STANCES = ['hostile', 'wary', 'friendly'];

// ------------------------------------------------------- villages and towns
// The Frontier plants villages; the Hall charters them into towns. A village
// sends up whatever the GROUND makes (see engine/terrain.js) and costs
// nothing. A town fills a standing order and EATS - which is what stops
// chartering from being strictly better. Matt, 2026-09-20: "I find it hard
// to believe I would ever choose to get rid of that card."
// A village gets a NAME, not the name of its ground (playtest 9: "what's the
// name of that town?" - "the mountains", in lower case on one screen and
// not the other). Dealt in order; the players can rename any of them.
// A much bigger list, dealt in order from a per-game shuffle (playtest 13,
// Matt: "pull from a much larger randomized list" - and "send a caravan to
// Wedding" is gone).
export const VILLAGE_NAMES = [
  'Highfold', 'Ashby', 'Cairnmoor', 'Saltmere', 'Thornwick', 'Lowmere', 'Brackenford',
  'Elderhythe', 'Stonecross', 'Fernlea', 'Gullhaven', 'Harrowgate', 'Ivyhold', 'Oakhurst', 'Westwater',
  'Marrowby', 'Coldharbour', 'Dunmere', 'Greywell', 'Hollin', 'Kestrelford', 'Larkhill', 'Netherby',
  'Ravensworth', 'Sedgemoor', 'Tarnwick', 'Umberlea', 'Whitlow', 'Yarrowdale', 'Alderbrook', 'Birchhollow',
  'Crowmarsh', 'Dovecote', 'Eastby', 'Foxglove', 'Goldthorpe', 'Hazelmere', 'Kingsmead', 'Lindholm',
  'Millbrook', 'Nettlecombe', 'Otterburn', 'Pennymoor', 'Quarrendon', 'Rushmere', 'Stanwick', 'Thistlebury',
  'Underhill', 'Wyndham', 'Ashgrove', 'Beckford', 'Carrowby', 'Deepdale', 'Ellerton', 'Fallowfield',
  'Glenmoor', 'Heathrow', 'Ingleby', 'Juniper Cross', 'Kelsey', 'Longstone', 'Mossend', 'Norbury',
];
export const VILLAGE_NAME_MAX = 24;
export const VILLAGE = {
  startSize: 1,
  // A village stops at 2; size 3 is a TOWN's (playtest 13, Matt: "villages
  // should just be 1 and 2, and you can only get to 3 by being a town" -
  // before this a size-3 village chartered into a size-3 town that ate, so
  // chartering a food village was strictly worse).
  maxSize: 2,
  // Three raiders getting through burns it down for good.
  raidsToBurn: 3,
  // A village may not be planted this close to the city or to another one.
  spacing: 2,
  // What a caravan brings back from one, and how much it feeds it.
  caravanLoad: 3, // only for readers with no settlement in hand; a caravan brings the settlement's own yield
  // Food a caravan carries out to make a village grow (playtest 11).
  caravanFood: 2,
  charterAtSize: 2,
  // A village that is left in peace GROWS on its own, this many nights at
  // a time. A caravan is the fast way; this is the patient one. Without it
  // the whole village -> town ladder hung on a caravan happening to reach
  // the right place, and towns were chartered about once in ten games.
  growsEvery: 3,
};
// CITIES (Matt, after playtest 12: "village, town, and then eventually you
// could establish a full city", "X cities where the limitation is some real
// world constraint, not just an arbitrary number"). A town of `fromSize`
// becomes a city if it has its own PATCH (no settlement or rival ground
// within `patch`, and land enough around it) and the capital can spare
// SETTLERS (`settlers` people, from a capital of `settlersFrom`). No limit
// but those: the island holds only so many patches, and people are growth.
// A city grows to `maxSize`, chooses a FOCUS (food, goods, coin, knowledge,
// or defence), may build one DISTRICT, eats more, and throws dice of its own.
export const CITY = {
  fromSize: 3, patch: 2, landAround: 4, settlers: 2, settlersFrom: 6,
  cost: { goods: 4, coin: 4 }, maxSize: 6, upkeepFood: 2, dice: 2,
  focuses: ['food', 'goods', 'coin', 'knowledge', 'defence'],
  districts: {
    market: { name: 'Market', text: '+1 coin every night', night: { coin: 1 } },
    school: { name: 'School', text: '+1 knowledge every night', night: { knowledge: 1 } },
    barracks: { name: 'Barracks', text: 'warbands trained here come one stronger', night: {} },
  },
  districtCost: { goods: 3 },
};

export const TOWN = {
  // A town is mouths: this much food every night, for the rest of the era.
  upkeepFood: 1,
  // A chartered town grows on from the village's cap (quiet nights or a
  // caravan), to here; a town of this size with its own ground may become a city.
  maxSize: 3,
  // What a standing order fills, per size.
  yieldPerSize: 1, // was 2: a size-3 town sent +6 coin a night (playtest 11)
  orders: ['food', 'goods', 'coin'],
};

// WHAT THEY ARE CALLED AT EACH SIZE. Matt, 2026-09-19: "I also don't love
// the term band... what if instead we had different names for the different
// levels of units, so a one was called something, a two was called something
// else." So growing is not a number going up, it is a promotion: a scout
// becomes a patrol becomes a party. Indexed by size, 1 to BAND.max.
export const BAND_NAMES = ['scout', 'patrol', 'party', 'company', 'column', 'host'];
// How many things are worth finding on one island: ruins, a grove, a wreck,
// a lode. Enough that exploring pays, few enough that it stays an event.
export const FINDS_PER_GAME = 10; // was 5, for an island three times the size
// Doing the same thing again today costs this much more each time. Matt,
// 2026-09-19: "the first time costs something, and then each time you do it
// after that, it costs a bit more. So you can't really just spam the same
// button over and over again." The Scholars' experiments have escalated
// like this since they got a price; this is the same idea, per action.
export const REPEAT_STEP = 1;

// ---------------------------------------------------------------- the deck
// How deep a newly added card is shuffled in: somewhere in the top N.
export const SEED_DEPTH = 5;
// The era's goal card is shuffled into this band of the starting deck
// (0-indexed from the top, inclusive).
export const GOAL_DEPTH = { min: 3, max: 6 };

// ------------------------------------------------------------ the great work
// The stages of whichever build goal the era chose (see cards.js GOALS).
// `civ.monument` keeps its name for old saves; it is "the great work".
export const MONUMENT_STAGES = 3;

// -------------------------------------------------------------- the end
// THERE IS NO WINNING ANY MORE, ONLY LASTING (Matt, 2026-09-21: "winning a
// game like this is something that will take place at the very far future").
// The deck refills when it runs out; wonders grant abilities instead of
// ending the era. `DECLINE` stays as a name for old saves' results.
export const LOSS = {
  REVOLT: 'revolt',       // happiness hit 0
  COLLAPSE: 'collapse',   // population hit 0
  SACKED: 'sacked',       // raiders reached a city with nothing left standing
  OUTPACED: 'outpaced',   // another people reached a victory first
  DECLINE: 'decline',     // (retired) the deck ran out before the goal stood
};

// --------------------------------------------------------------- the lore
export const PUBLISH_COST = { knowledge: 1 };
export const HINT_COST = { knowledge: 2 };
export const THEORY_HOLDS = { knowledge: 2 };   // a theory that holds pays back
export const THEORY_DEBUNKED = { happiness: -1 }; // a theory that falls costs face
// (Held theories used to open the deep bench and the long game. Since
// 2026-10-15 the machines do - see machines.js.)
// INGREDIENTS (Matt, 2026-10-15: "one piece that we're not simulating here
// is the ingredients from Alchemists... it could just be goods"). Every
// experiment burns a good; a machine's firing has its own price (`fuel` in
// machines.js).
export const MIX_COST = { goods: 1 };

// The Stewards alone count in SHIFTS - half a person's day each. Matt: "I fear that if they only get
// one [worker], that's not going to be a very fun worker placement. It's
// almost like a single person needs to be able to be divisible, so each token
// you place represents half a day of work at a place." So every worker the
// Stewards are given becomes two tokens to place, and everything on their
// board is priced in those tokens: staffing a building takes one, building or
// annexing takes two - a whole person for the day.
export const SHIFTS_PER_WORKER = 2;
// THE FRONTIER TOO (Matt, 2026-10-15: "free up the Frontier a bit. Let's
// have a single worker have two shifts for the Frontier. Like how we do for
// the Stewards. Maybe we just call them 'actions'"). A Frontier worker is two
// ACTIONS - each a unit's step, hunt or fight - priced as orders were.
export const ACTIONS_PER_WORKER = 2;
const TOKENS_PER_WORKER = { trade: SHIFTS_PER_WORKER, military: ACTIONS_PER_WORKER };
export const tokensPerWorker = (role) => TOKENS_PER_WORKER[role] || 1;
export const shiftsFor = (role, workers) => workers * tokensPerWorker(role);
/**
 * And back again, for anywhere that counts PEOPLE rather than tokens - the
 * role tabs, the table's summary, the Hall asking who should get a levy.
 * Matt, 2026-09-20: "up at that top level, it should remain workers. It
 * shouldn't get doubled like that." A lone shift is somebody with half a
 * day left in them, so it rounds up to one person.
 */
export const workersFor = (role, tokens) => Math.ceil(tokens / tokensPerWorker(role));
/** What a role's day is counted in, for the words on its board: shift, action, worker. */
export const tokenWord = (role, n = 2) => {
  const one = { trade: 'shift', military: 'action' }[role] || 'worker';
  return n === 1 ? one : `${one}s`;
};

// THE RACE (Matt, 2026-09-25: "the end game win goals, like just steal them
// right from Civilization"). When it opens the group picks one victory to
// chase; every number here is a dial. See DESIGN.md "THE ARC".
// THE VICTORIES ARE ON THE TABLE FROM THE FIRST MONTH (Matt, 2026-09-27:
// "you're taking the concept of the race too literally... the victory
// conditions are such long-term goals that they are far in the distance
// for most of the time, but you can start building towards them"). No
// race opens, nobody picks one: all four are shown with their progress
// from year 1, the campaign ends the moment any is met - by the town or by
// a people - and ten years is the tuning target, not a cap.
// Tuned 2026-09-28 against the bots: with the old numbers every victory
// landed in years 1-3 (Prosperity by round 24, Dominion by gifting both
// neighbours). Each is a long haul now, and the bots are the measure.
export const VICTORY = {
  legacyWonders: 8,      // Legacy: this many wonders stand (17 exist)
  enlightenTechs: 6,     // Enlightenment: this many last-tier technologies (6 exist; bots won with 4 in year 6)
  prosperPeople: 12,     // Prosperity: a big town (12 people needs 3 Houses since housing)...
  prosperHappy: 8,       // ...this happy AFTER the night's fade (9 is unreachable: 10 fades to 8)...
  prosperNights: 36,     // ...for this many nights running (three seasons)
};
// Rivals race for wonders too: a met, unbroken rival finishes one every
// this many nights, and it is gone for you. A people that finishes
// RIVAL_LEGACY of them has won Legacy for itself, and the campaign is lost:
// at one every 24 nights from about year 2, that is year 10 or so.
export const RIVAL_WONDER_EVERY = 24;
export const RIVAL_LEGACY = 4;
// ...and never takes one if it would leave fewer than this many for you
// beyond what Legacy still needs - a long game must never run out of wonders.
export const WONDER_RESERVE = 3;

// The game log keeps this many entries; older ones drop off and `logBase`
// counts them, so positions stay absolute (a very long game must not ship a
// megabyte of history through Firebase on every action).
export const LOG_KEEP = 250;

// ---------------------------------------------------------------- the year
// See engine/calendar.js. The year card offers this many session goals in
// the first month; the winter tale offers this many trades at Midwinter.
export const YEAR_GOALS_OFFERED = 2;
export const TALES_OFFERED = 2;
// A retired counselor who served at least this many months leaves a
// keepsake card in the Hall's deck (Matt, 2026-09-27: "that seems pretty
// fun"). Terms run 4-8, so about half of them earn one.
export const KEEPSAKE_AFTER = 6;
// The Harvest: the fields' and villages' lump on top of the night's usual
// take, as a multiple of what they make in a night.
export const HARVEST_TIMES = 2; // was 3: "here is everything you could ever want, forever" (playtest 11)
// Midwinter's feast.
export const MIDWINTER = { happiness: 2 };
